import { Component } from 'vue'
import { Commit } from 'vuex'
import { CustomModalProps, IModalBase } from '@/interfaces'
import { ModalLayer, ModalType } from '@/components/modals/modal-types'

// in case i18n is needed in the future...
// import i18n from '@/i18n'
// i18n.global.t('some-translation', { someParam: someValue })

export type VueComponentContent = Component
export type VueComponentHeader = Component
export type VueComponentFooter = Component

export interface IModalProps {
  modalType: ModalType | null
  componentContent?: VueComponentContent | null
  componentHeader?: VueComponentHeader | null
  componentFooter?: VueComponentFooter | null
  props?: CustomModalProps
  payload?: Record<string, unknown>
}

export interface IModal {
  modalLayer: ModalLayer.Main
  isMainModalOpen: boolean
}

export type IModalState = IModal & IModalBase

const basicState = {
  componentContent: null,
  componentHeader: null,
  componentFooter: null,
  props: {},
  payload: {}
}

export const modal = {
  namespaced: true,
  state: (): IModalState => ({
    modalLayer: ModalLayer.Main,
    isMainModalOpen: false,
    modalState: basicState,
    loading: false,
    isSubmitting: false,
    isCanceling: false,
    modalType: null
  }),
  mutations: {
    openModal(state: IModalState, modalPayload: IModalProps) {
      const { props, modalType, componentContent, componentHeader, componentFooter, payload } = modalPayload
      state.loading = false
      state.isSubmitting = false
      state.isCanceling = false
      state.modalType = modalType
      state.modalState = {
        componentContent: componentContent || null,
        componentHeader: componentHeader || null,
        componentFooter: componentFooter || null,
        props: props || {},
        payload: payload || {}
      }
      state.isMainModalOpen = true
    },

    closeModal(state: IModalState) {
      state.modalState = basicState
      state.modalType = null
      state.isMainModalOpen = false
    },

    hideModal(state: IModalState) {
      state.isMainModalOpen = false
    },

    revealModal(state: IModalState) {
      state.isMainModalOpen = true
    },

    startLoading(state: IModalState) {
      state.loading = true
    },

    stopLoading(state: IModalState) {
      state.loading = false
    },

    setActionValidation(state: IModalState, payload: boolean) {
      if (state.modalState.props.footerProps) {
        state.modalState.props.footerProps.isConfirmValid = payload
      }
    },

    setModalProps(state: IModalState, payload: Record<string, unknown>) {
      state.modalState.props = { ...state.modalState.props, ...payload }
    },
    setModalPayload(state: IModalState, payload: Record<string, unknown>) {
      state.modalState.payload = { ...payload }
    },
    startSubmitting(state: IModalState) {
      state.isSubmitting = true
    },
    stopSubmitting(state: IModalState) {
      state.isSubmitting = false
    },
    startCanceling(state: IModalState) {
      state.isCanceling = true
    },
    stopCanceling(state: IModalState) {
      state.isCanceling = false
    },
    setExtraProps(state: IModalState, payload: Record<string, unknown>) {
      state.modalState.props = {
        ...state.modalState.props,
        extraProps: {
          ...(state.modalState.props?.extraProps ?? {}),
          ...payload
        }
      }
    }
  },
  actions: {
    openModal({ commit }: { commit: Commit }, payload: IModalProps) {
      commit('openModal', payload)
    },
    closeModal({ commit }: { commit: Commit }) {
      commit('closeModal')
    },
    hideModal({ commit }: { commit: Commit }) {
      commit('hideModal')
    },
    revealModal({ commit }: { commit: Commit }) {
      commit('revealModal')
    },
    startLoading({ commit }: { commit: Commit }) {
      commit('startLoading')
    },
    stopLoading({ commit }: { commit: Commit }) {
      commit('stopLoading')
    },
    setActionValidation({ commit }: { commit: Commit }, payload: boolean) {
      commit('setActionValidation', payload)
    },
    setModalPayload({ commit }: { commit: Commit }, payload: Record<string, unknown>) {
      commit('setModalPayload', payload)
    },
    setModalProps({ commit }: { commit: Commit }, payload: IModalProps) {
      commit('setModalProps', payload)
    },
    setExtraProps({ commit }: { commit: Commit }, payload: IModalProps) {
      commit('setExtraProps', payload)
    },
    startSubmitting({ commit }: { commit: Commit }) {
      commit('startSubmitting')
    },
    stopSubmitting({ commit }: { commit: Commit }) {
      commit('stopSubmitting')
    },
    startCanceling({ commit }: { commit: Commit }) {
      commit('startCanceling')
    },
    stopCanceling({ commit }: { commit: Commit }) {
      commit('stopCanceling')
    }
  },
  getters: {
    getModal: (state: IModalState) => () => {
      return state
    },
    getLoadingState: (state: IModalState) => () => {
      return state.loading
    },
    getIsMainModalOpen: (state: IModalState) => () => {
      return state.isMainModalOpen
    },
    getModalType: (state: IModalState) => () => {
      return state.modalType
    },
    getModalProps: (state: IModalState) => () => {
      return state.modalState.props
    },
    getModalPayload: (state: IModalState) => () => {
      return state.modalState.payload
    },
    getModalExtraProps: (state: IModalState) => () => {
      return state.modalState.props.extraProps
    },
    getIsSubmitting: (state: IModalState) => () => {
      return state.isSubmitting
    },
    getIsCanceling: (state: IModalState) => () => {
      return state.isCanceling
    }
  }
}
